import { useEffect, useState } from "react";
import "./ContactConsentModal.css";

/**
 * Buyer-side consent gate that appears the first time a buyer tries to
 * reveal any seller contact (within a 24h window). Required by PA's
 * "directory only, no mediation" stance — the buyer explicitly
 * acknowledges that PA isn't a party to whatever happens after.
 *
 *   <ContactConsentModal open={...} sellerName="..."
 *                        onAgree={...} onCancel={...} />
 */
export default function ContactConsentModal({ open, sellerName, onAgree, onCancel }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) setChecked(false);
    const onKey = (e) => { if (e.key === "Escape" && open) onCancel?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className="ccm-backdrop" onClick={onCancel}>
      <div className="ccm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="ccm-header">
          <span className="ccm-title">Before you continue</span>
          <button className="ccm-close" aria-label="Close" onClick={onCancel}>×</button>
        </div>

        <p className="ccm-body">
          You're about to view contact details for
          {sellerName ? <strong> {sellerName}</strong> : " this seller"} so
          you can reach them off-platform. <strong>PlayAware does not take
          part in this transaction.</strong>
        </p>
        <ul className="ccm-points">
          <li>We don't process payments, hold funds, or mediate disputes.</li>
          <li>We don't verify a seller's stock, identity, or claims beyond what's shown on their profile.</li>
          <li>Any agreement, delivery, or refund happens entirely between you and the seller.</li>
          <li>You're responsible for compliance with your game's terms of service.</li>
        </ul>

        <label className="ccm-check">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>
            I understand and agree. PlayAware is not responsible for any
            outcome from contacting this seller.
          </span>
        </label>

        <div className="ccm-actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" disabled={!checked} onClick={onAgree}>
            Reveal contact
          </button>
        </div>

        <div className="ccm-footnote">
          This consent will be remembered for 24 hours.
        </div>
      </div>
    </div>
  );
}
